import {
  ConnectionBaseFragment,
  useActivateConnectionSettingsQuery,
  useConnectionQuery,
} from "@/apollo/types";
import { useIntegrationsMap } from "@/integrations";

type InputOptions =
  | { initialIntegrationId?: string; initialConnectionId?: never }
  | { initialIntegrationId?: string | never; initialConnectionId?: string }
  | { initialIntegrationId?: never; initialConnectionId?: never };

export function useValidInputProps(props: InputOptions): {
  validInputProps: {
    integrationId?: string;
    connection?: ConnectionBaseFragment;
    connectionSettings?: Record<string, any>;
  };
  isLoading: boolean;
  isValidConnectionId?: boolean | undefined;
  isValidIntegrationId?: boolean | undefined;
} {
  const integrationsRegistry = useIntegrationsMap();

  const { data: connectionData, loading: isLoadingConnection } =
    useConnectionQuery({
      fetchPolicy: "network-only",
      variables: {
        id: props.initialConnectionId || "",
      },
      skip: !props.initialConnectionId,
    });

  const { data: connectionSettingsData, loading: isLoadingConnectionSettings } =
    useActivateConnectionSettingsQuery({
      variables: {
        input: {
          id: props.initialConnectionId || "",
        },
      },
      skip: !props.initialConnectionId,
    });

  const isLoading = isLoadingConnection || isLoadingConnectionSettings;
  if (isLoading) {
    return {
      validInputProps: {},
      isLoading,
    };
  }

  if (props.initialConnectionId) {
    const isValidConnectionId =
      !isLoadingConnection && connectionData !== undefined;
    if (connectionData && connectionSettingsData) {
      const connectionSettings =
        connectionSettingsData.getActivateSettings.connectionSettings;
      return {
        validInputProps: {
          integrationId: connectionData.connection.integrationId,
          connection: connectionData.connection,
          connectionSettings,
        },
        isLoading,
        isValidConnectionId,
      };
    } else {
      return {
        validInputProps: {},
        isLoading,
        isValidConnectionId,
      };
    }
  }
  if (props.initialIntegrationId) {
    const integration = integrationsRegistry.get(props.initialIntegrationId);
    const isValidIntegrationId = integration !== undefined;
    if (isValidIntegrationId) {
      return {
        validInputProps: { integrationId: integration.id },
        isLoading,
        isValidIntegrationId,
      };
    } else {
      return {
        validInputProps: {},
        isLoading,
        isValidIntegrationId,
      };
    }
  }

  return {
    validInputProps: {},
    isLoading: false,
  };
}
